import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Divider,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Switch,
  Autocomplete,
  Paper,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  FormGroup,
  FormControlLabel,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import RouterIcon from '@mui/icons-material/Router';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { getPersonnel as getPersonnelSql } from '../../../../services/migration/personnelDataService';
// Firebase sadece personel verisi için kullanılıyor (personel henüz SQL'e taşınmadı)
import salesService from '../../../../services/salesService';
import accountingService from '../../../../services/accountingService';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../../common/guide/GuideButton';
import PavoRestP2PDeviceSelectDialog from '../settings/PavoRestP2PDeviceSelectDialog';
import type { PavoDevice } from '../../../../services/pavoApiService';
import CashierSettingsTab from '../settings/CashierSettingsTab';
import SalesTerminalDevicesSection from '../settings/SalesTerminalDevicesSection';
import {
  QuickCreateCashboxButton,
  QuickCreateBankAccountButton,
  useBranchName,
  buildCashCashboxName,
  buildPosBankName,
  type CreatedCashbox,
  type CreatedBankAccount,
} from '../../../common/QuickCreatePaymentTarget';

// Doğrudan Personnel ve PersonnelPermissions tiplerini tanımlayalım
interface Personnel {
  id: string;
  name: string;
  timestamp?: number;
}

interface PersonnelPermissions {
  changePrice: boolean;
  applyDiscount: boolean;
  cancel: boolean;
  viewReports: boolean;
  warehouseOperations: boolean;
  viewAccountBalance?: boolean;
  listAccounts?: boolean;
}

// PaymentSettingsTab'den alınan tipler
export type PaymentOptionType = 'cash' | 'pos' | 'openAccount' | 'voucher' | 'receipt' | 'bonus' | 'accrual';

export interface PaymentOption {
  id: string;
  type: PaymentOptionType;
  label: string;
  enabled: boolean;
  cashboxId?: string | null;
  bankAccountId?: string | null;
  defaultAccountId?: string | null;
  timestamp?: number;
  subAccountId?: string | null;
  relatedAccount?: string;
}

// Kasa, hesap ve banka hesapları tipleri
interface Cashbox {
  id: string;
  name: string;
}

interface Account {
  id: string;
  companyName: string;
}

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  isPosAccount?: boolean;
  subAccounts?: { [key: string]: SubAccountForModal };
}

interface SubAccountForModal {
  id: string;
  accountName: string;
  accountNumber: string;
  isPosAccount?: boolean;
}

// Özel PykPaymentSettingsTab bileşeni oluşturalım (PaymentSettingsTab kopyasıdır ama ön ödemeli sistem kısmı kaldırılmıştır)
interface PykPaymentSettingsTabProps {
  paymentOptions: PaymentOption[];
  allCashboxes: Cashbox[];
  allAccounts: Account[];
  allBankAccounts: BankAccount[];
  loading: boolean;
  onPaymentOptionFieldChange: (id: string, field: keyof PaymentOption, value: any) => void;
  onAddPaymentOption: (type: PaymentOptionType) => void;
  onDeletePaymentOption: (idToDelete: string) => void;
  // ✅ YENİ: Bağlantı alanından hızlı kasa/POS hesabı oluşturma
  companyId?: string | null;
  branchId?: string | null;
  onCashboxCreated?: (cashbox: CreatedCashbox) => void;
  onBankAccountCreated?: (bankAccount: CreatedBankAccount) => void;
}

const PykPaymentSettingsTab: React.FC<PykPaymentSettingsTabProps> = ({
  paymentOptions,
  allCashboxes,
  allAccounts,
  allBankAccounts,
  loading,
  onPaymentOptionFieldChange,
  onAddPaymentOption,
  onDeletePaymentOption,
  companyId,
  branchId,
  onCashboxCreated,
  onBankAccountCreated
}) => {
  const { t } = useTranslation('salesSystem');
  // Bu sekmede kullanılacak helper fonksiyonlar
  const cashOptionExists = paymentOptions.some(option => option.type === 'cash');

  // ── Bağlantı alanından hızlı kasa/banka oluşturma ──────────────────────
  const branchName = useBranchName(branchId);
  const quickCreateEnabled = Boolean(companyId && onCashboxCreated && onBankAccountCreated);
  const existingCashboxes = React.useMemo<CreatedCashbox[]>(
    () => allCashboxes.map(cb => ({ id: cb.id, name: cb.name })),
    [allCashboxes]
  );
  const existingBankAccounts = React.useMemo<CreatedBankAccount[]>(
    () => allBankAccounts.map(ba => ({ id: ba.id, bankName: ba.bankName, accountNumber: ba.accountNumber })),
    [allBankAccounts]
  );

  // Nakit kasalarını filtrele - Debug için
  React.useEffect(() => {
    console.log("Tüm kasalar:", allCashboxes);
  }, [allCashboxes]);

  return (
    <Paper elevation={0} sx={{ p: 1.5 }}>
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5}}>
        <Typography variant="subtitle1" gutterBottom>{t('pykScreenSettingsModal.tabs.paymentMethods')}</Typography>
        <Box>
          <Button 
            size="small" 
            onClick={() => onAddPaymentOption('cash')} 
            variant="outlined" 
            sx={{mr: 1}} 
            disabled={cashOptionExists}
          >
            {t('paymentSettingsTab.addCash')}
          </Button>
          <Button 
            size="small" 
            onClick={() => onAddPaymentOption('pos')} 
            variant="outlined"
          >
            {t('paymentSettingsTab.addPos')}
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 1.5 }}/>
      
      {paymentOptions.length === 0 && !loading ? (
        <Typography variant="caption" color="text.secondary">{t('paymentSettingsTab.noPaymentMethod')}</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{fontWeight: 'bold'}}>{t('paymentSettingsTab.table.type')}</TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>{t('paymentSettingsTab.table.labelName')}</TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>{t('paymentSettingsTab.table.connection')}</TableCell>
                <TableCell sx={{fontWeight: 'bold', textAlign: 'center'}}>{t('paymentSettingsTab.table.status')}</TableCell>
                <TableCell sx={{fontWeight: 'bold', textAlign: 'right'}}>{t('paymentSettingsTab.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentOptions.map((option) => {
                const usedCashboxIds = paymentOptions
                  .filter(opt => opt.id !== option.id && opt.type === 'cash' && opt.cashboxId)
                  .map(opt => opt.cashboxId);
                const usedBankAccountIds = paymentOptions
                  .filter(opt => opt.id !== option.id && opt.type === 'pos' && opt.bankAccountId)
                  .map(opt => opt.bankAccountId);
                
                let typeLabel = '';
                switch (option.type) {
                  case 'cash': typeLabel = t('paymentSettingsTab.paymentTypes.cash'); break;
                  case 'pos': typeLabel = 'POS'; break;
                  case 'openAccount': typeLabel = t('paymentSettingsTab.paymentTypes.openAccount'); break;
                  default: typeLabel = option.type;
                }
                
                return (
                  <TableRow key={option.id}>
                    <TableCell sx={{textTransform: 'capitalize'}}>{typeLabel}</TableCell>
                    <TableCell>
                      <TextField 
                        variant="standard"
                        size="small"
                        fullWidth
                        value={option.label}
                        onChange={(e) => onPaymentOptionFieldChange(option.id, 'label', e.target.value)}
                        sx={{ minWidth: 150 }}
                      />
                    </TableCell>
                    <TableCell>
                      {option.type === 'cash' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Autocomplete
                            size="small"
                            options={allCashboxes.filter(cb => 
                              !usedCashboxIds.includes(cb.id) || cb.id === option.cashboxId
                            )}
                            getOptionLabel={(cb) => cb.name}
                            value={allCashboxes.find(cb => cb.id === option.cashboxId) || null}
                            onChange={(e, newValue) => onPaymentOptionFieldChange(option.id, 'cashboxId', newValue?.id ?? null)}
                            renderInput={(params) => <TextField {...params} variant="standard" placeholder={t('paymentSettingsTab.placeholders.selectCashbox')} />}
                            sx={{ minWidth: 180 }}
                          />
                          {quickCreateEnabled && (
                            <QuickCreateCashboxButton
                              companyId={companyId}
                              branchId={branchId}
                              existingCashboxes={existingCashboxes}
                              suggestedName={buildCashCashboxName(branchName)}
                              onCreated={(cashbox) => {
                                onCashboxCreated?.(cashbox);
                                onPaymentOptionFieldChange(option.id, 'cashboxId', cashbox.id);
                              }}
                            />
                          )}
                        </Box>
                      )}
                      {option.type === 'pos' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Autocomplete
                            size="small"
                            options={[
                              // POS özellikli ana hesaplar
                              ...allBankAccounts
                                .filter(ba => ba.isPosAccount && (!usedBankAccountIds.includes(ba.id) || ba.id === option.bankAccountId))
                                .map(ba => ({
                                  id: ba.id,
                                  bankName: `${ba.bankName} - ${ba.accountNumber}`,
                                  accountNumber: ba.accountNumber,
                                  isPosAccount: true,
                                  isSubAccount: false,
                                  mainAccountId: ba.id,
                                  subAccountId: null
                                })),
                              // POS özellikli alt hesaplar
                              ...allBankAccounts.flatMap(ba => 
                                ba.subAccounts ? 
                                Object.entries(ba.subAccounts)
                                  .filter(([subId, sub]) => sub.isPosAccount)
                                  .map(([subId, sub]) => ({ 
                                    id: `${ba.id}_sub_${subId}`,
                                    bankName: `${ba.bankName} - ${sub.accountName}`,
                                    accountNumber: sub.accountNumber,
                                    isPosAccount: true,
                                    isSubAccount: true,
                                    mainAccountId: ba.id,
                                    subAccountId: subId
                                  }))
                                : []
                              )
                            ]}
                            getOptionLabel={(opt: any) => opt.bankName}
                            value={(() => {
                              if (option.bankAccountId && option.subAccountId) {
                                // Seçili bir alt hesap varsa onu bul
                                const mainAcc = allBankAccounts.find(ba => ba.id === option.bankAccountId);
                                if (!mainAcc) {
                                  console.warn(`Ana hesap bulunamadı: ${option.bankAccountId}`);
                                  return null;
                                }
                                
                                const subAcc = mainAcc.subAccounts?.[option.subAccountId];
                                if (mainAcc && subAcc) {
                                  return {
                                    id: `${mainAcc.id}_sub_${option.subAccountId}`,
                                    bankName: `${mainAcc.bankName} - ${subAcc.accountName}`,
                                    accountNumber: subAcc.accountNumber,
                                    isPosAccount: true,
                                    isSubAccount: true,
                                    mainAccountId: mainAcc.id,
                                    subAccountId: option.subAccountId
                                  };
                                }
                              } else if (option.bankAccountId) {
                                // Sadece ana hesap seçiliyse onu bul
                                const mainAcc = allBankAccounts.find(ba => ba.id === option.bankAccountId);
                                if (mainAcc) {
                                  return {
                                    id: mainAcc.id,
                                    bankName: `${mainAcc.bankName} - ${mainAcc.accountNumber}`,
                                    accountNumber: mainAcc.accountNumber,
                                    isPosAccount: true,
                                    isSubAccount: false,
                                    mainAccountId: mainAcc.id,
                                    subAccountId: null
                                  };
                                }
                              }
                              return null;
                            })()}
                            onChange={(e, newValue: any) => {
                              console.log("Seçilen banka hesabı:", newValue);
                              if (newValue) {
                                // Ana hesabı ve alt hesabı ayarla
                                onPaymentOptionFieldChange(option.id, 'bankAccountId', newValue.mainAccountId);
                                onPaymentOptionFieldChange(option.id, 'subAccountId', newValue.isSubAccount ? newValue.subAccountId : null);
                              } else {
                                // Değerler null olarak temizlenir
                                onPaymentOptionFieldChange(option.id, 'bankAccountId', null);
                                onPaymentOptionFieldChange(option.id, 'subAccountId', null);
                              }
                            }}
                            renderInput={(params) => <TextField {...params} variant="standard" placeholder={t('paymentSettingsTab.placeholders.selectPosAccount')} />}
                            sx={{ minWidth: 220 }}
                            isOptionEqualToValue={(opt: any, val: any) => {
                              if (!opt || !val) return false;
                              if (opt.isSubAccount && val.isSubAccount) {
                                return opt.id === val.id; // Alt hesap karşılaştırması
                              }
                              return opt.mainAccountId === val.mainAccountId; // Ana hesap karşılaştırması
                            }}
                          />
                          {quickCreateEnabled && (
                            <QuickCreateBankAccountButton
                              companyId={companyId}
                              branchId={branchId}
                              existingBankAccounts={existingBankAccounts}
                              suggestedBankName={buildPosBankName(branchName)}
                              onCreated={(bankAccount) => {
                                onBankAccountCreated?.(bankAccount);
                                onPaymentOptionFieldChange(option.id, 'bankAccountId', bankAccount.id);
                                onPaymentOptionFieldChange(option.id, 'subAccountId', null);
                              }}
                            />
                          )}
                        </Box>
                      )}
                      {option.type === 'openAccount' && (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={option.enabled}
                        onChange={(e) => onPaymentOptionFieldChange(option.id, 'enabled', e.target.checked)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => onDeletePaymentOption(option.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

// Özel PykPersonnelSettingsTab bileşeni
interface PykPersonnelSettingsTabProps {
  allPersonnel: Personnel[];
  selectedPersonnelIds: string[];
  selectedPersonnelForPermissions: string | null;
  personnelPermissionsMap: { [personnelId: string]: PersonnelPermissions };
  loading: boolean;
  onPersonnelSelectionChange: (newSelectedIds: string[]) => void;
  onSelectPersonnelForPermissions: (personnelId: string) => void;
  onPermissionChange: (personnelId: string, permission: string, value: boolean) => void;
}

const PykPersonnelSettingsTab: React.FC<PykPersonnelSettingsTabProps> = ({
  allPersonnel,
  selectedPersonnelIds,
  selectedPersonnelForPermissions,
  personnelPermissionsMap,
  loading,
  onPersonnelSelectionChange,
  onSelectPersonnelForPermissions,
  onPermissionChange
}) => {
  const { t } = useTranslation('salesSystem');
  return (
    <Paper elevation={0} sx={{ p: 1.5 }}>
      <Typography variant="subtitle1" gutterBottom>{t('personnelSettingsTab.title')}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {t('personnelSettingsTab.description')}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      {allPersonnel.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          {/* Sol Taraf: Personel Arama ve Listeleme */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              {t('personnelSettingsTab.personnelSelection')}
            </Typography>
            <Autocomplete
              multiple
              size="small"
              id="assigned-personnel-autocomplete"
              options={allPersonnel}
              getOptionLabel={(option) => option.name}
              value={allPersonnel.filter(p => selectedPersonnelIds.includes(p.id))}
              onChange={(event, newValue) => {
                const newSelectedIds = newValue.map(p => p.id);
                onPersonnelSelectionChange(newSelectedIds);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('personnelSettingsTab.searchSelectPersonnel')}
                  fullWidth
                />
              )}
              renderTags={() => null} // Prevent default tags, using list below
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ fontSize: '0.875rem' }}>
                  <Checkbox
                    size="small"
                    style={{ marginRight: 4 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              )}
              loading={loading}
              sx={{ mb: 2 }}
            />

            {selectedPersonnelIds.length > 0 && (
              <Box>
                <Typography variant="caption" display="block" gutterBottom sx={{ fontWeight: 'medium' }}>
                  {t('personnelSettingsTab.selectedPersonnel')}
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    maxHeight: 400,
                    overflowY: 'auto'
                  }}
                >
                  <List dense disablePadding>
                    {allPersonnel
                      .filter(p => selectedPersonnelIds.includes(p.id))
                      .map(p => (
                        <ListItem
                          key={p.id}
                          selected={selectedPersonnelForPermissions === p.id}
                          button
                          onClick={() => onSelectPersonnelForPermissions(p.id)}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                const newSelectedIds = selectedPersonnelIds.filter(id => id !== p.id);
                                onPersonnelSelectionChange(newSelectedIds);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                          divider
                        >
                          <ListItemText primary={p.name} />
                        </ListItem>
                      ))}
                  </List>
                </Paper>
              </Box>
            )}
          </Box>

          {/* Sağ Taraf: Yetki Ayarları */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              {t('personnelSettingsTab.permissionSettings')}
            </Typography>

            {selectedPersonnelForPermissions ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  minHeight: 200,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  {allPersonnel.find(p => p.id === selectedPersonnelForPermissions)?.name || t('personnelSettingsTab.selectedPersonnelFallback')}
                </Typography>

                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.changePrice || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'changePrice', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.changePrice')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.applyDiscount || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'applyDiscount', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.applyDiscount')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.cancel || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'cancel', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.cancel')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.viewReports || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'viewReports', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.viewReports')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.warehouseOperations || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'warehouseOperations', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.warehouseOperations')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.viewAccountBalance || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'viewAccountBalance', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.viewAccountBalance')}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.listAccounts || false}
                        onChange={(e) => onPermissionChange(selectedPersonnelForPermissions, 'listAccounts', e.target.checked)}
                      />
                    }
                    label={t('personnelSettingsTab.permissions.listAccounts')}
                  />
                </FormGroup>
              </Paper>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  minHeight: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.02)'
                }}
              >
                <Typography variant="body2" color="text.secondary" align="center">
                  {t('personnelSettingsTab.selectPersonnelHint')}
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      ) : (
        <Typography variant="caption">{t('personnelSettingsTab.noPersonnel')}</Typography>
      )}
    </Paper>
  );
};

interface PykScreenSettingsModalProps {
  open: boolean;
  onClose: () => void;
  screenId: string;
  companyId: string | null;
  screenName: string;
}

const PykScreenSettingsModal: React.FC<PykScreenSettingsModalProps> = ({
  open,
  onClose,
  screenId,
  companyId,
  screenName,
}) => {
  const { t } = useTranslation('salesSystem');
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);
  
  // Payment Options için gerekli state'ler
  const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([]);
  const [allCashboxes, setAllCashboxes] = useState<Cashbox[]>([]);
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const [allBankAccounts, setAllBankAccounts] = useState<BankAccount[]>([]);
  // PYK ekranının şubesi — ödeme sekmesindeki hızlı kasa/banka oluşturmada kullanılır
  const [pykBranchId, setPykBranchId] = useState<string | null>(null);
  
  // Yazarkasa ayarları
  // PYK ekranında kullanılacak yazarkasa: 'none' | 'pavopay' | 'ingenico_5000f'
  const [pykFiscalDevice, setPykFiscalDevice] = useState<'none' | 'pavopay' | 'ingenico_5000f'>('none');
  // Nakit iadede fişlenmemiş satışları Pavo cihazına göndermeden kaydet (fatura/gün sonu/stok yine işlenir)
  const [skipPavoForCashRefund, setSkipPavoForCashRefund] = useState<boolean>(false);

  // Ingenico: POS bakiye yüklemede kesilecek ürün (yüklenen tutar kadar satılır)
  const [ingenicoBalanceProduct, setIngenicoBalanceProduct] = useState<{ id: string; name: string; vatRate: number; departmentNo: number } | null>(null);
  const [productSearchInput, setProductSearchInput] = useState<string>('');
  const [productOptions, setProductOptions] = useState<Array<{ id: string; name: string; vatRate: number; departmentNo: number }>>([]);
  const [productSearchLoading, setProductSearchLoading] = useState<boolean>(false);

  // Diğer Ayarlar - Oturum Başlat/Kapat butonlarını PYK ekranında göster (varsayılan açık)
  const [showSessionButtons, setShowSessionButtons] = useState<boolean>(true);

  // Login İşlemleri - kasiyer PIN sistemi (varsayılan kapalı)
  const [useCashierSystem, setUseCashierSystem] = useState<boolean>(false);

  // Rest-P2P (uzaktan PavoPay) ayarları
  const [useRestP2PForPayments, setUseRestP2PForPayments] = useState<boolean>(false);
  const [pavoRestP2PDeviceScreenCode, setPavoRestP2PDeviceScreenCode] = useState<string | null>(null);
  const [pavoRestP2PDeviceName, setPavoRestP2PDeviceName] = useState<string | null>(null);
  const [restP2PSelectOpen, setRestP2PSelectOpen] = useState<boolean>(false);

  // Personel yönetimi için gereken state'ler
  const [allPersonnel, setAllPersonnel] = useState<Personnel[]>([]);
  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState<string[]>([]);
  const [selectedPersonnelForPermissions, setSelectedPersonnelForPermissions] = useState<string | null>(null);
  const [personnelPermissionsMap, setPersonnelPermissionsMap] = useState<{[personnelId: string]: PersonnelPermissions}>({});

  // Ekran ayarlarını çekme
  useEffect(() => {
    if (!open || !companyId || !screenId) return;

    const loadSettings = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // PYK ekran ayarlarını SQL'den çek
        const screenIdNum = parseInt(screenId, 10);
        if (!isNaN(screenIdNum)) {
          const pykScreen = await salesService.getPykScreenById(screenIdNum);
          // Hızlı kasa/banka oluşturmada kayıtları bu şubeye bağlamak için gerekli
          setPykBranchId(pykScreen.BranchId || null);
          if (pykScreen.SettingsJson) {
            try {
              const settings = JSON.parse(pykScreen.SettingsJson);
              
              if (settings.paymentOptions) {
                const optionsList = Array.isArray(settings.paymentOptions)
                  ? settings.paymentOptions
                  : Object.entries(settings.paymentOptions).map(([id, option]: [string, any]) => ({ id, ...option }));
                setPaymentOptions(optionsList);
              }
              
              if (settings.assignedPersonnel) {
                if (Array.isArray(settings.assignedPersonnel)) {
                  setSelectedPersonnelIds(settings.assignedPersonnel);
                } else {
                  setSelectedPersonnelIds(
                    Object.entries(settings.assignedPersonnel)
                      .filter(([, assigned]: [string, any]) => assigned === true)
                      .map(([id]) => id)
                  );
                }
              }
              
              if (settings.personnelPermissions) {
                setPersonnelPermissionsMap(settings.personnelPermissions);
              }

              // Yazarkasa cihazı: yeni alan yoksa eski usePavoPay'den türet
              const resolvedDevice: 'none' | 'pavopay' | 'ingenico_5000f' =
                settings.pykFiscalDevice || (settings.usePavoPay ? 'pavopay' : 'none');
              setPykFiscalDevice(resolvedDevice);
              setIngenicoBalanceProduct(settings.ingenicoBalanceProduct || null);

              setSkipPavoForCashRefund(Boolean(settings.skipPavoForCashRefund));

              // Oturum butonları: tanımlı değilse varsayılan AÇIK (true)
              setShowSessionButtons(settings.showSessionButtons !== false);

              setUseCashierSystem(Boolean(settings.useCashierSystem));

              if (settings.useRestP2PForPayments !== undefined) {
                setUseRestP2PForPayments(Boolean(settings.useRestP2PForPayments));
              }
              if (settings.pavoRestP2PDeviceScreenCode !== undefined) {
                setPavoRestP2PDeviceScreenCode(settings.pavoRestP2PDeviceScreenCode || null);
              }
              if (settings.pavoRestP2PDeviceName !== undefined) {
                setPavoRestP2PDeviceName(settings.pavoRestP2PDeviceName || null);
              }
              
              console.log("PYK ekran ayarları SQL'den yüklendi:", settings);
            } catch (parseErr) {
              console.error("SettingsJson parse hatası:", parseErr);
            }
          } else {
            console.log("PYK ekranı için henüz ayar yok, boş başlatılıyor.");
            setPaymentOptions([]);
            setSelectedPersonnelIds([]);
            setPersonnelPermissionsMap({});
            setUseCashierSystem(false);
          }
        }
        
        // Kasaları SQL'den yükle
        try {
          const cashboxData = await accountingService.getCashboxes();
          if (Array.isArray(cashboxData)) {
            const cashboxList: Cashbox[] = cashboxData.map((c: any) => ({
              id: (c?.Id || c?.id)?.toString() || '',
              name: c?.Name || c?.name || t('pykScreenSettingsModal.fallback.unnamedCashbox')
            })).filter(c => c.id);
            setAllCashboxes(cashboxList);
            console.log(`✅ ${cashboxList.length} kasa SQL'den yüklendi`);
          }
        } catch (err) {
          console.error("Kasa listesi yüklenemedi:", err);
        }

        // Hesapları SQL'den yükle
        try {
          const accountsData = await accountingService.getAccounts();
          if (Array.isArray(accountsData)) {
            const accountsList: Account[] = accountsData
              .filter((acc: any) => (acc.IsActive ?? acc.isActive) !== false)
              .map((acc: any) => ({
                id: (acc.Id || acc.id)?.toString() || '',
                companyName: acc.CompanyName || acc.companyName || t('pykScreenSettingsModal.fallback.unnamedAccount')
              })).filter(a => a.id);
            setAllAccounts(accountsList);
            console.log(`✅ ${accountsList.length} hesap SQL'den yüklendi`);
          }
        } catch (err) {
          console.error("Hesap listesi yüklenemedi:", err);
        }

        // Banka hesaplarını SQL'den yükle
        try {
          const bankData = await accountingService.getBankAccounts();
          if (Array.isArray(bankData)) {
            const bankList: BankAccount[] = bankData.map((data: any) => {
              const subAccounts: { [key: string]: SubAccountForModal } = {};
              const subAccountsArr = data.SubAccounts || data.subAccounts || [];
              if (Array.isArray(subAccountsArr)) {
                subAccountsArr.forEach((sub: any) => {
                  const subId = (sub.Id || sub.id)?.toString() || '';
                  if (subId) {
                    subAccounts[subId] = {
                      id: subId,
                      accountName: sub.AccountName || sub.accountName || t('pykScreenSettingsModal.fallback.unnamedSubAccount'),
                      accountNumber: sub.AccountNumber || sub.accountNumber || '-',
                      isPosAccount: sub.IsPosAccount ?? sub.isPosAccount ?? false,
                    };
                  }
                });
              }
              return {
                id: (data.Id || data.id)?.toString() || '',
                bankName: data.BankName || data.bankName || t('pykScreenSettingsModal.fallback.unnamedBank'),
                accountNumber: data.AccountNumber || data.accountNumber || '',
                isPosAccount: data.IsPosAccount ?? data.isPosAccount ?? false,
                subAccounts
              };
            }).filter(b => b.id);
            setAllBankAccounts(bankList);
            console.log(`✅ ${bankList.length} banka hesabı SQL'den yüklendi`);
          }
        } catch (err) {
          console.error("Banka hesap listesi yüklenemedi:", err);
        }

        // Faz 3: personel listesi SQL'den (personnelDataService).
        try {
          const sqlPersonnel = await getPersonnelSql(companyId);
          const personnelList = (sqlPersonnel || []).map((pData: any) => ({
            id: pData.id, // Firebase UID
            name: pData.name || t('pykScreenSettingsModal.fallback.unnamedPersonnel')
          }));
          setAllPersonnel(personnelList);
          console.log(`✅ ${personnelList.length} personel SQL'den yüklendi`);
        } catch (err) {
          console.error("Personel listesi yüklenemedi:", err);
        }
      } catch (err) {
        console.error("Ekran ayarları yüklenirken hata:", err);
        setError(t('pykScreenSettingsModal.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [open, companyId, screenId]);

  // Ingenico ürün arama (debounce'lu) — sadece Ingenico seçiliyken çalışır
  useEffect(() => {
    if (pykFiscalDevice !== 'ingenico_5000f') return;
    const term = productSearchInput.trim();
    if (term.length < 2) return;
    let active = true;
    setProductSearchLoading(true);
    const handle = setTimeout(async () => {
      try {
        const results = await salesService.searchProducts(term);
        if (!active) return;
        const mapped = (results || [])
          .map((p: any) => ({
            id: (p.Id ?? p.id ?? '').toString(),
            name: p.Name ?? p.name ?? '',
            vatRate: Number(p.VatRate ?? p.vatRate ?? 0) || 0,
            departmentNo: Number(p.DepartmentNo ?? p.departmentNo ?? 0) || 0,
          }))
          .filter((p: any) => p.id && p.name);
        setProductOptions(mapped);
      } catch (e) {
        console.error('[PYK Ayar] Ürün arama hatası:', e);
      } finally {
        if (active) setProductSearchLoading(false);
      }
    }, 350);
    return () => { active = false; clearTimeout(handle); };
  }, [productSearchInput, pykFiscalDevice]);

  // Tab değişimi
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Ödeme yöntemi field değişimini işle
  const handlePaymentOptionFieldChange = (id: string, field: keyof PaymentOption, value: any) => {
    setPaymentOptions(currentOptions =>
      currentOptions.map(option =>
        option.id === id ? { ...option, [field]: value } : option
      )
    );
  };

  // Ödeme sekmesindeki "+" ile oluşturulan kasa/banka kaydını listelere ekler
  const handleQuickCreatedCashbox = (cashbox: CreatedCashbox) => {
    setAllCashboxes(prev =>
      prev.some(cb => cb.id === cashbox.id) ? prev : [...prev, cashbox]
    );
  };

  const handleQuickCreatedBankAccount = (bankAccount: CreatedBankAccount) => {
    setAllBankAccounts(prev =>
      prev.some(ba => ba.id === bankAccount.id)
        ? prev
        : [
            ...prev,
            {
              id: bankAccount.id,
              bankName: bankAccount.bankName,
              accountNumber: bankAccount.accountNumber,
              isPosAccount: true,
            },
          ]
    );
  };
  
  // Yeni ödeme yöntemi ekle
  const handleAddPaymentOption = (type: PaymentOptionType) => {
    const newOption: PaymentOption = {
      id: Date.now().toString(),
      type,
      label: type === 'cash' ? t('paymentSettingsTab.paymentTypes.cash') : type === 'pos' ? 'POS' : t('paymentSettingsTab.paymentTypes.openAccount'),
      enabled: true,
      timestamp: Date.now()
    };
    setPaymentOptions([...paymentOptions, newOption]);
  };
  
  // Ödeme yöntemi sil
  const handleDeletePaymentOption = (idToDelete: string) => {
    setPaymentOptions(currentOptions => 
      currentOptions.filter(option => option.id !== idToDelete)
    );
  };

  // Personel seçimini değiştir
  const handlePersonnelSelectionChange = (newSelectedIds: string[]) => {
    setSelectedPersonnelIds(newSelectedIds);
    // Eğer şu anda seçili personel artık seçili değilse, seçimi kaldır
    if (selectedPersonnelForPermissions && !newSelectedIds.includes(selectedPersonnelForPermissions)) {
      setSelectedPersonnelForPermissions(null);
    }
  };

  // Yetki ayarları için personel seç
  const handleSelectPersonnelForPermissions = (personnelId: string) => {
    setSelectedPersonnelForPermissions(personnelId);
    
    // Eğer bu personelin henüz yetki ayarları yoksa, varsayılan değerlerle oluştur
    if (!personnelPermissionsMap[personnelId]) {
      setPersonnelPermissionsMap(prev => ({
        ...prev,
        [personnelId]: {
          changePrice: false,
          applyDiscount: false,
          cancel: false,
          viewReports: false,
          warehouseOperations: false,
          viewAccountBalance: false,
          listAccounts: false
        }
      }));
    }
  };

  // Personel yetkisini değiştir
  const handlePermissionChange = (personnelId: string, permission: string, value: boolean) => {
    setPersonnelPermissionsMap(prev => ({
      ...prev,
      [personnelId]: {
        ...prev[personnelId],
        [permission]: value
      }
    }));
  };

  // Ayarları kaydetme
  const handleSaveSettings = async () => {
    if (!companyId || !screenId) {
      setError(t('pykScreenSettingsModal.errors.missingCompanyOrScreen'));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const isPavo = pykFiscalDevice === 'pavopay';
      const isIngenico = pykFiscalDevice === 'ingenico_5000f';
      const settingsToSave = {
        paymentOptions: paymentOptions,
        assignedPersonnel: selectedPersonnelIds,
        personnelPermissions: personnelPermissionsMap,
        pykFiscalDevice: pykFiscalDevice,
        // Geriye dönük uyumluluk: eski okuyucular usePavoPay bekliyor
        usePavoPay: isPavo,
        ingenicoBalanceProduct: isIngenico ? ingenicoBalanceProduct : null,
        skipPavoForCashRefund: isPavo ? skipPavoForCashRefund : false,
        useRestP2PForPayments: isPavo ? useRestP2PForPayments : false,
        pavoRestP2PDeviceScreenCode: isPavo ? pavoRestP2PDeviceScreenCode : null,
        pavoRestP2PDeviceName: isPavo ? pavoRestP2PDeviceName : null,
        showSessionButtons: showSessionButtons,
        useCashierSystem: useCashierSystem,
      };
      
      const screenIdNum = parseInt(screenId, 10);
      if (isNaN(screenIdNum)) {
        setError(t('pykScreenSettingsModal.errors.invalidScreenId'));
        return;
      }

      await salesService.updatePykScreen({
        Id: screenIdNum,
        SettingsJson: JSON.stringify(settingsToSave)
      });
      
      console.log("PYK ekran ayarları SQL'e kaydedildi:", settingsToSave);
      
      setSuccessMessage(t('pykScreenSettingsModal.messages.saved'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Ayarlar kaydedilirken hata:", err);
      setError(t('pykScreenSettingsModal.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h6">{t('pykScreenSettingsModal.title', { screenName })}</Typography>
          <GuideButton guideKey="sales.pykSettings" color="inherit" size="small" />
        </Box>
        <IconButton color="inherit" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2, minHeight: '400px' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        ) : (
          <>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
              <Tab label={t('pykScreenSettingsModal.tabs.paymentMethods')} />
              <Tab label={t('pykScreenSettingsModal.tabs.personnelAssignment')} />
              <Tab label={t('pykScreenSettingsModal.tabs.fiscalSettings')} />
              <Tab label={t('pykScreenSettingsModal.tabs.otherSettings')} />
            </Tabs>
            
            <Divider sx={{ mb: 3 }} />
            
            {tabValue === 0 && (
              <PykPaymentSettingsTab
                paymentOptions={paymentOptions}
                allCashboxes={allCashboxes}
                allAccounts={allAccounts}
                allBankAccounts={allBankAccounts}
                loading={loading}
                onPaymentOptionFieldChange={handlePaymentOptionFieldChange}
                onAddPaymentOption={handleAddPaymentOption}
                onDeletePaymentOption={handleDeletePaymentOption}
                companyId={companyId}
                branchId={pykBranchId}
                onCashboxCreated={handleQuickCreatedCashbox}
                onBankAccountCreated={handleQuickCreatedBankAccount}
              />
            )}
            
            {tabValue === 1 && (
              <Box>
                <PykPersonnelSettingsTab
                  allPersonnel={allPersonnel}
                  selectedPersonnelIds={selectedPersonnelIds}
                  selectedPersonnelForPermissions={selectedPersonnelForPermissions}
                  personnelPermissionsMap={personnelPermissionsMap}
                  loading={loading}
                  onPersonnelSelectionChange={handlePersonnelSelectionChange}
                  onSelectPersonnelForPermissions={handleSelectPersonnelForPermissions}
                  onPermissionChange={handlePermissionChange}
                />

                <Divider sx={{ my: 3 }} />

                <Box sx={{ px: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('pykScreenSettingsModal.login.cashierSystemTitle')}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useCashierSystem}
                        onChange={(e) => setUseCashierSystem(e.target.checked)}
                      />
                    }
                    label={t('pykScreenSettingsModal.login.useCashierSystem')}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('pykScreenSettingsModal.login.useCashierSystemHint')}
                  </Typography>

                  {useCashierSystem && companyId && !isNaN(parseInt(screenId, 10)) && (
                    <CashierSettingsTab
                      screenId={parseInt(screenId, 10)}
                      screenType="Pyk"
                      companyId={companyId}
                    />
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ px: 1.5 }}>
                  <SalesTerminalDevicesSection
                    companyId={companyId}
                    linkedScreenId={isNaN(parseInt(screenId, 10)) ? null : parseInt(screenId, 10)}
                    deviceMode="sales_pyk"
                    screenName={screenName}
                  />
                </Box>
              </Box>
            )}
            
            {tabValue === 2 && (
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t('pykScreenSettingsModal.fiscal.title')}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel>Yazarkasa</InputLabel>
                  <Select
                    value={pykFiscalDevice}
                    label="Yazarkasa"
                    onChange={(e) => {
                      const val = e.target.value as 'none' | 'pavopay' | 'ingenico_5000f';
                      setPykFiscalDevice(val);
                    }}
                  >
                    <MenuItem value="none">Yok</MenuItem>
                    <MenuItem value="pavopay">PavoPay POS</MenuItem>
                    <MenuItem value="ingenico_5000f">Ingenico 5000F</MenuItem>
                  </Select>
                </FormControl>

                {pykFiscalDevice === 'none' && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
                    {t('pykScreenSettingsModal.fiscal.disabledInfo')}
                  </Typography>
                )}

                {pykFiscalDevice === 'ingenico_5000f' && (
                  <Box sx={{ mt: 1 }}>
                    <Autocomplete
                      size="small"
                      options={productOptions}
                      loading={productSearchLoading}
                      getOptionLabel={(opt) => opt?.name || ''}
                      isOptionEqualToValue={(opt, val) => opt.id === val.id}
                      value={ingenicoBalanceProduct}
                      onChange={(_e, newVal) => setIngenicoBalanceProduct(newVal)}
                      onInputChange={(_e, val) => setProductSearchInput(val)}
                      filterOptions={(x) => x}
                      noOptionsText="Ürün aramak için yazın..."
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Bakiye Yükleme Ürünü (POS satışı için)"
                          placeholder="Ürün adı ara..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {productSearchLoading ? <CircularProgress size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    {ingenicoBalanceProduct && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Seçili ürün: <strong>{ingenicoBalanceProduct.name}</strong> · KDV %{ingenicoBalanceProduct.vatRate} · Departman {ingenicoBalanceProduct.departmentNo || '-'}
                      </Typography>
                    )}
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Bakiye iadesi <strong>her zaman nakit</strong> olarak ve kasadan yapılır; Ingenico'ya iade/fiş gönderilmez.
                    </Alert>
                    {!ingenicoBalanceProduct && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        POS ile bakiye yükleyebilmek için bir ürün seçmelisiniz.
                      </Alert>
                    )}
                  </Box>
                )}

                {pykFiscalDevice === 'pavopay' && (
                  <>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {t('pykScreenSettingsModal.fiscal.enabledInfo')}
                    </Alert>

                {/* Nakit iadede Pavo'ya fiş göndermeden satışı kaydet */}
                <Box
                  sx={{
                    mt: 3,
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: skipPavoForCashRefund ? 'warning.main' : 'divider',
                    bgcolor: skipPavoForCashRefund ? 'rgba(237,108,2,0.04)' : 'transparent',
                    transition: 'all 0.2s',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptLongIcon sx={{ color: skipPavoForCashRefund ? 'warning.main' : 'text.disabled', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Nakit İadede Pavo'ya Fiş Gönderme
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                          Saf nakit iadede (kartta PavoPay POS yüklemesi yokken) fişlenmemiş satışlar Pavo cihazına gönderilmez.
                          Satış kaydı (fatura, gün sonu) yine oluşturulur; stok zaten satış anında düşülür.
                          İade işlemi fiş kesilmeden yapılabilir.
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={skipPavoForCashRefund}
                      onChange={(e) => setSkipPavoForCashRefund(e.target.checked)}
                      color="warning"
                    />
                  </Box>
                </Box>

                {/* Rest-P2P (Uzaktan PavoPay) bölümü — usePavoPay'den bağımsız çalışır */}
                <Box
                  sx={{
                    mt: 3,
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: useRestP2PForPayments ? '#7c4dff' : 'divider',
                    bgcolor: useRestP2PForPayments ? 'rgba(124,77,255,0.04)' : 'transparent',
                    transition: 'all 0.2s',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RouterIcon sx={{ color: useRestP2PForPayments ? '#7c4dff' : 'text.disabled', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: useRestP2PForPayments ? '#7c4dff' : 'text.primary' }}>
                          Rest-P2P Modu (Uzaktan PavoPay)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PYK ödemeleri ve fiş kesimi seçilen Pavo cihazına SignalR üzerinden gönderilir.
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={useRestP2PForPayments}
                      onChange={(e) => setUseRestP2PForPayments(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#7c4dff' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#7c4dff' },
                      }}
                    />
                  </Box>

                  {useRestP2PForPayments && (
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PointOfSaleIcon />}
                        onClick={() => setRestP2PSelectOpen(true)}
                        sx={{
                          borderColor: '#7c4dff',
                          color: '#7c4dff',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            backgroundColor: 'rgba(124,77,255,0.08)',
                            borderColor: '#651fff',
                          },
                        }}
                      >
                        {pavoRestP2PDeviceScreenCode ? 'Cihazı Değiştir' : 'Cihaz Seç'}
                      </Button>
                      {pavoRestP2PDeviceScreenCode ? (
                        <Chip
                          icon={<PointOfSaleIcon sx={{ fontSize: '1rem !important' }} />}
                          label={
                            pavoRestP2PDeviceName
                              ? `${pavoRestP2PDeviceName} • ${pavoRestP2PDeviceScreenCode}`
                              : pavoRestP2PDeviceScreenCode
                          }
                          size="small"
                          onDelete={() => {
                            setPavoRestP2PDeviceScreenCode(null);
                            setPavoRestP2PDeviceName(null);
                          }}
                          sx={{ bgcolor: 'rgba(124,77,255,0.12)', color: '#7c4dff', fontWeight: 600 }}
                        />
                      ) : (
                        <Typography variant="caption" color="error">
                          Lütfen bir Rest-P2P cihazı seçin.
                        </Typography>
                      )}
                    </Box>
                  )}

                  {useRestP2PForPayments && (
                    <Box sx={{ mt: 1.5, p: 1, borderRadius: 1, bgcolor: 'rgba(124,77,255,0.06)', border: '1px dashed rgba(124,77,255,0.4)' }}>
                      <Typography variant="caption" sx={{ color: '#5e35b1', fontWeight: 500, display: 'block', lineHeight: 1.45 }}>
                        Aktif: Bakiye Yükle (POS), Bakiye İade (POS Avans) ve Fiş Kes işlemleri seçilen Pavo cihazına yönlendirilir.
                        Bu özellik için Pavo cihaz uygulamasının güncel sürümde olması gerekir.
                      </Typography>
                    </Box>
                  )}
                </Box>

                <PavoRestP2PDeviceSelectDialog
                  open={restP2PSelectOpen}
                  onClose={() => setRestP2PSelectOpen(false)}
                  companyId={companyId ?? undefined}
                  currentDeviceCode={pavoRestP2PDeviceScreenCode}
                  onSelect={(device: PavoDevice) => {
                    setPavoRestP2PDeviceScreenCode(device.deviceCode);
                    setPavoRestP2PDeviceName(device.deviceName);
                  }}
                />
                  </>
                )}
              </Box>
            )}

            {tabValue === 3 && (
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t('pykScreenSettingsModal.other.title')}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showSessionButtons}
                        onChange={(e) => setShowSessionButtons(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={t('pykScreenSettingsModal.other.showSessionButtons')}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                    {t('pykScreenSettingsModal.other.showSessionButtonsHint')}
                  </Typography>
                </Box>
              </Box>
            )}

            {successMessage && (
              <Alert severity="success" sx={{ mt: 2 }}>{successMessage}</Alert>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {t('quickSaleSettingsModal.footer.cancel')}
        </Button>
        <Button 
          onClick={handleSaveSettings} 
          variant="contained" 
          color="primary"
          disabled={loading || saving}
        >
          {saving ? <CircularProgress size={24} /> : t('quickSaleSettingsModal.footer.saveChanges')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PykScreenSettingsModal; 